import { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import {
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { useTheme } from "@/hooks/use-theme";
import {
  ArrowRight,
  ArrowLeft,
  Tray,
  CheckCircle,
  ShieldCheck,
  Clock,
  ChartBar,
  MapPin,
  Users,
  ChatTeardropText,
  Building,
  ShootingStar,
  House,
  Video,
  Handshake,
  Medal,
  Globe,
  DeviceMobile,
  Headphones,
  Target,
  Megaphone,
  Brain,
} from "@phosphor-icons/react";

import Lottie from "lottie-react";

import logo from "@/assets/images/UrbNetLogoSemFundo.png";
import heroLogo from "@/assets/images/UrbNet2LOGO.png";
import registerInLocal from "@/assets/images/register_in_local.json";
import encaminhamento from "@/assets/images/encaminhamento.json";
import apoieOutras from "@/assets/images/apoie_outras.json";
import analyseData from "@/assets/images/analyse-data.json";
import { LandingAtmosphere } from "@/components/landing/LandingAtmosphere";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { PublicHeader } from "@/components/landing/PublicHeader";
import {
  CitySilhouette,
  InfrastructureLines,
  AccentOrb,
} from "@/components/landing/LandingVisuals";

// Editorial imagery — Unsplash photos for infrastructure / municipal context
// (free to use under Unsplash License, saved locally for reliability)
import heroCity from "@/assets/images/landing/hero-city.jpg";
import cityHall from "@/assets/images/landing/city-hall.jpg";
import cityMap from "@/assets/images/landing/city-map.jpg";
import cityAerial from "@/assets/images/landing/city-aerial.jpg";
import dashboard from "@/assets/images/landing/dashboard-urbnet.png";
import handshake from "@/assets/images/landing/handshake.jpg";
import teamMeeting from "@/assets/images/landing/team-meeting.jpg";
import publicSpace from "@/assets/images/landing/public-space.jpg";

const IMAGES = {
  heroCity,
  cityHall,
  cityMap,
  cityAerial,
  dashboard,
  handshake,
  teamMeeting,
  publicSpace,
};

/* ============================================================================
 * Primitives — adapted from the "Modern Luxury" editorial layout
 * ========================================================================== */

const Reveal = ({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.25 }}
    transition={{ duration: 0.4, delay, ease: [0.22, 1, 0.36, 1] }}
    style={{ willChange: "transform, opacity" }}
    className={className}
  >
    {children}
  </motion.div>
);

/* Signature "engraved metal" text-clip gradient (radial highlight + dark linear),
 * adapted from the reference's radial+linear headings to the UrbNet palette. */
const metallicText: React.CSSProperties = {
  backgroundImage:
    "radial-gradient(75% 140% at 50% 0%, hsl(var(--primary) / 0.60) 0%, hsl(var(--primary) / 0) 55%), linear-gradient(180deg, hsl(var(--foreground)) 0%, hsl(200 24% 26%) 52%, hsl(200 16% 42%) 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
  color: "transparent",
};

/* Glassy card sheen (mirrors linear-gradient(150.53deg,#FFF -8%, transparent 86%)) */
const glassCard: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(150deg, hsl(0 0% 100%) -8%, hsl(0 0% 100% / 0) 86%)",
};

/* Big metallic display heading */
const Display = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <span style={metallicText} className={className}>
    {children}
  </span>
);

/* Editorial image panel with rounded corners, warm overlay and soft shadow */
const ImageCard = ({
  src,
  alt,
  className = "",
  aspect = "video",
  overlay = true,
  objectFit = "cover",
}: {
  src: string;
  alt: string;
  className?: string;
  aspect?: "auto" | "video" | "square" | "portrait" | "wide";
  overlay?: boolean;
  objectFit?: "cover" | "contain";
}) => {
  const aspectClasses = {
    auto: "",
    video: "aspect-video",
    square: "aspect-square",
    portrait: "aspect-[3/4]",
    wide: "aspect-[16/7]",
  };
  return (
    <div
      className={`group relative overflow-hidden rounded-[32px] bg-muted shadow-[0_18px_48px_-32px_hsl(var(--foreground)/0.3)] ring-1 ring-black/[0.04] ${aspectClasses[aspect]} ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        {...(aspect !== "auto" ? { width: "800", height: "600" } : {})}
        className={`w-full object-${objectFit} transition-transform duration-300 ease-out group-hover:scale-105 ${aspect === "auto" ? "h-auto" : "h-full"}`}
      />
      {overlay && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, hsl(35 40% 70% / 0.10) 0%, transparent 40%, hsl(40 30% 90% / 0.20) 100%)",
            transform: "translateZ(0)",
          }}
        />
      )}
    </div>
  );
};

/* Refined uppercase kicker with side hairlines */
const Kicker = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.45em] text-primary/70">
    <span className="h-px w-8 bg-primary/40" />
    {children}
    <span className="h-px w-8 bg-primary/40" />
  </span>
);

/* High-impact CTA button — brand colors, rounded, glow, arrow */
const LuxButton = ({
  children,
  onClick,
  full = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  full?: boolean;
}) => (
  <button
    onClick={onClick}
    style={{
      backgroundImage:
        "linear-gradient(135deg, hsl(var(--secondary)) 0%, hsl(var(--primary)) 100%)",
    }}
    className={`group relative isolate inline-flex items-center justify-center gap-2 overflow-hidden rounded-full px-8 py-4 text-sm font-bold uppercase tracking-[0.08em] text-primary-foreground shadow-[0_10px_35px_-10px_hsl(var(--primary)/0.65)] ring-2 ring-white/35 transition-all duration-300 hover:-translate-y-1 hover:scale-[1.03] hover:shadow-[0_18px_55px_-12px_hsl(var(--primary)/0.75)] active:translate-y-0 active:scale-[0.98] sm:px-10 sm:py-5 sm:text-[15px] ${
      full ? "w-full" : "w-full min-w-[240px] sm:w-auto"
    }`}
  >
    <span className="relative z-10">{children}</span>
  </button>
);

/* ============================================================================
 * Content — UrbNet Ouvidoria texts
 * ========================================================================== */

/* Rotating category accents — brand color rhythm for harmonic contrast */
const ACCENTS = [
  {
    icon: "bg-primary/10 text-primary ring-primary/15 group-hover:bg-primary group-hover:text-primary-foreground",
    bar: "bg-gradient-to-r from-primary to-primary-light",
    hoverShadow: "hover:shadow-[0_24px_50px_-26px_hsl(var(--primary)/0.45)]",
    text: "text-primary",
    line: "bg-primary/40",
    cardBg: "bg-primary/[0.10] hover:bg-primary/[0.16]",
  },
  {
    icon: "bg-secondary/10 text-secondary ring-secondary/15 group-hover:bg-secondary group-hover:text-secondary-foreground",
    bar: "bg-gradient-to-r from-secondary to-secondary-light",
    hoverShadow: "hover:shadow-[0_24px_50px_-26px_hsl(var(--secondary)/0.45)]",
    text: "text-secondary",
    line: "bg-secondary/40",
    cardBg: "bg-secondary/[0.10] hover:bg-secondary/[0.16]",
  },
  {
    icon: "bg-tertiary/15 text-tertiary ring-tertiary/25 group-hover:bg-tertiary group-hover:text-tertiary-foreground",
    bar: "bg-gradient-to-r from-tertiary to-tertiary/70",
    hoverShadow: "hover:shadow-[0_24px_50px_-26px_hsl(var(--tertiary)/0.45)]",
    text: "text-tertiary",
    line: "bg-tertiary/40",
    cardBg: "bg-tertiary/[0.10] hover:bg-tertiary/[0.16]",
  },
];

const FEATURES = [
  { icon: Clock, title: "Protocolo rastreável do registro à resposta" },
  { icon: Video, title: "Acompanhamento em tempo real" },
  { icon: ShootingStar, title: "Atendimento simples para o cidadão" },
  { icon: House, title: "Demandas mapeadas no território" },
  { icon: ChartBar, title: "Indicadores reais do dia a dia" },
  { icon: ShieldCheck, title: "Conformidade com a LGPD" },
];

const MODULES = [
  {
    title: "Recebimento Multicanal",
    bullets: [
      "Registro de denúncias, reclamações, sugestões e elogios",
      "Anexos de fotos e evidências pelo cidadão",
      "Geolocalização precisa da ocorrência",
      "Protocolo único e rastreável desde o primeiro contato",
    ],
  },
  {
    title: "Triagem Inteligente",
    bullets: [
      "Classificação por categoria, tipo e prioridade",
      "Encaminhamento automático ao setor responsável",
      "Validação e checagem de manifestações",
      "Distribuição de demandas entre agentes",
    ],
  },
  {
    title: "Acompanhamento & Gestão",
    bullets: [
      "Painel de agentes com todas as demandas em andamento",
      "Status atualizado e visível para o cidadão",
      "Prazos e alertas de pendências",
      "Histórico completo de cada atendimento",
    ],
  },
  {
    title: "Resolução & Transparência",
    bullets: [
      "Resposta e encaminhamento registrados",
      "Avaliação do atendimento pelo cidadão",
      "Indicadores de desempenho e prestação de contas",
      "Relatórios e evolução por período",
    ],
  },
];

const BENEFITS = [
  "Receba e organize manifestações em um único canal",
  "Reduza o tempo de resposta com triagem automática",
  "Acompanhe cada protocolo com geolocalização",
  "Veja indicadores do atendimento por período",
  "Mantenha cidadão e gestão informados",
  "Atenda à legislação de ouvidoria com segurança",
];

const CAROUSEL = [
  { animation: registerInLocal, label: "No território" },
  { animation: encaminhamento, label: "Encaminhamento" },
  { animation: apoieOutras, label: "Participação cidadã" },
  { animation: analyseData, label: "Dados & indicadores" },
];

const SERVICE_EXAMPLES = [
  { icon: MapPin, title: "Zeladoria colaborativa", desc: "Receba e gerencie ocorrências urbanas diretamente dos cidadãos." },
];

const CAPABILITIES = [
  { icon: DeviceMobile, title: "App e web", desc: "O cidadão registra pelo canal que preferir. A gestão acessa tudo em um só painel." },
  { icon: MapPin, title: "Localização exata da demanda", desc: "Cada manifestação chega com geolocalização e foto do local, facilitando a ação da equipe." },
  { icon: ChartBar, title: "Indicadores e prazos", desc: "Acompanhe quantidade, status e tempo de resposta por setor, bairro ou período." },
  { icon: ShieldCheck, title: "LGPD e auditoria", desc: "Registro de ações, controle de acesso e proteção de dados pessoais." },
  { icon: Building, title: "Visão entre secretarias", desc: "Distribua demandas entre órgãos e acompanhe o andamento em um painel unificado." },
  { icon: Headphones, title: "Suporte do time UrbNet", desc: "Implementação guiada, treinamento e atendimento para sua equipe usar bem a ferramenta." },
];

const HIGHLIGHTS = [
  { icon: DeviceMobile, title: "Atendimento nos canais do cidadão", desc: "Web, aplicativo e WhatsApp para que qualquer pessoa possa registrar uma demanda com facilidade." },
  { icon: MapPin, title: "Foto + localização do problema", desc: "O cidadão envia a imagem e o ponto no mapa. A equipe municipal sabe exatamente onde atuar." },
  { icon: ChartBar, title: "Decisões com dados do dia a dia", desc: "Painéis simples mostram o que chegou, o que está atrasado e onde a cidade precisa de mais atenção." },
  { icon: ShieldCheck, title: "Transparência com segurança", desc: "Status público das demandas, histórico de ações e proteção de informações sigilosas." },
];

/* ============================================================================
 * Page
 * ========================================================================== */

const LandingPage = () => {
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement>(null);
  const [slide, setSlide] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const rawHeroY = useTransform(scrollYProgress, [0, 1], [0, 45]);
  const rawHeroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const heroY = useSpring(rawHeroY, { stiffness: 40, damping: 28, restDelta: 0.5 });
  const heroOpacity = useSpring(rawHeroOpacity, { stiffness: 50, damping: 30, restDelta: 0.02 });

  const isEmbedded =
    typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).get("embed") === "1" ||
      window.self !== window.top);

  const goToApp = () => navigate("/login");
  const goToOrcamento = () => navigate("/solicitar-orcamento");
  const prev = () => setSlide((s) => (s - 1 + CAROUSEL.length) % CAROUSEL.length);
  const next = () => setSlide((s) => (s + 1) % CAROUSEL.length);

  useEffect(() => {
    document.title = "UrbNet · Ouvidoria Municipal Digital";
  }, []);

  // Landing page is always light — restore previous theme on unmount
  const { theme, setTheme } = useTheme();
  const originalTheme = useRef(theme);
  useEffect(() => {
    if (theme !== "light") {
      setTheme("light");
    }
  }, [theme, setTheme]);
  useEffect(() => {
    return () => setTheme(originalTheme.current);
  }, [setTheme]);

  return (
    <div className="relative isolate min-h-screen overflow-x-hidden bg-transparent text-foreground selection:bg-primary/20">
      <LandingAtmosphere />

      {/* ---------------------------------------------------------------- Nav */}
      {!isEmbedded && <PublicHeader />}

      {/* --------------------------------------------------------------- Hero */}
      <section
        ref={heroRef}
        className={`isolate relative flex items-center justify-center overflow-hidden ${
          isEmbedded ? "pt-6" : "pt-[72px] md:pt-[80px]"
        }`}
      >
        {/* Hero background — UrbNet brand gradient */}
        <div
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(at 20% 10%, rgba(200, 230, 210, 0.72) 0px, transparent 45%), radial-gradient(at 80% 20%, rgba(190, 210, 230, 0.68) 0px, transparent 45%), radial-gradient(at 60% 80%, rgba(210, 230, 220, 0.64) 0px, transparent 48%), radial-gradient(at 10% 90%, rgba(240, 245, 230, 0.58) 0px, transparent 50%), #f5fafc",
          }}
        />

        {/* Editorial hero content */}
        <motion.div
          className="relative z-10 mx-auto flex max-w-7xl flex-col items-center px-6 py-16 text-center"
        >
          <div className="relative flex flex-col items-center">
            <div className="absolute inset-y-[-2rem] left-1/2 -z-10 w-screen -translate-x-1/2 bg-gradient-to-r from-primary to-secondary" />
          <Reveal>
            <div className="mb-8 flex flex-col items-center sm:mb-10">
              <img
                src={heroLogo}
                alt="UrbNet"
                className="h-20 w-auto drop-shadow-[0_12px_40px_rgba(0,0,0,0.1)] sm:h-24"
              />
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <h1 className="font-heading text-4xl font-bold uppercase leading-[0.98] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl">
              A ouvidoria da sua cidade
              <br />
              <span className="text-white">pronta para atender</span>
            </h1>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-4xl text-base font-medium leading-relaxed text-white/90 sm:mt-8 sm:text-lg md:text-xl">
              Receba manifestações, organize o atendimento e responda à população
              com mais clareza e velocidade — tudo em um só painel feito para
              prefeituras.
            </p>
          </Reveal>
          </div>

          <Reveal delay={0.28}>
            <div className="mt-8 flex flex-col items-center gap-4 sm:mt-11">
              <LuxButton onClick={goToApp}>Acessar plataforma</LuxButton>
              <span className="font-heading text-xs font-bold text-foreground/70 sm:text-sm">
                Entrada exclusiva para gestores públicos
              </span>
            </div>
          </Reveal>
        </motion.div>

      </section>

      {/* ----------------------------------------------- Para quem se destina */}
      <section id="destina" className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <InfrastructureLines />
        <AccentOrb color="tertiary" className="absolute -right-12 top-12 h-48 w-48 md:-right-24 md:top-24 md:h-80 md:w-80" />
        <Reveal>
          <div className="relative overflow-hidden rounded-[28px] border border-white/60 bg-gradient-to-br from-white via-card to-primary/[0.04] shadow-[0_18px_48px_-32px_hsl(var(--foreground)/0.22)] ring-1 ring-black/[0.03] sm:rounded-[40px]">
            <div className="grid grid-cols-1 gap-8 p-6 sm:p-10 lg:grid-cols-2 lg:gap-12 lg:p-16">
              <div>
                <h2 className="font-heading text-3xl font-bold leading-tight sm:text-4xl md:text-5xl text-foreground">
                  Para quem a UrbNet Ouvidoria serve
                </h2>
                <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">
                  Prefeituras, secretarias e órgãos públicos que precisam organizar
                  manifestações, responder com mais rapidez e manter a população
                  informada sobre o que acontece na cidade.
                </p>
                <div className="mt-10 grid gap-4 sm:grid-cols-3">
                  {[
                    { icon: Building, t: "Gestão municipal", a: ACCENTS[0] },
                    { icon: Users, t: "Cidadãos", a: ACCENTS[1] },
                    { icon: ChatTeardropText, t: "Equipes de ouvidoria", a: ACCENTS[2] },
                  ].map((i) => (
                    <div
                      key={i.t}
                      className={`group flex items-center gap-3 rounded-2xl border border-border/50 px-4 py-4 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${i.a.cardBg}`}
                    >
                      <span className="text-sm font-semibold text-foreground">{i.t}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-center">
                <ImageCard
                  src={IMAGES.cityMap}
                  alt="Mapeamento territorial"
                  aspect="square"
                  className="w-full max-w-md"
                />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ---------------------------------------------------- O que oferece */}
      <section id="recursos" className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <AccentOrb color="primary" className="absolute -left-12 top-1/4 h-64 w-64 md:-left-24 md:top-1/3 md:h-96 md:w-96" />
        <div className="relative grid grid-cols-1 items-center gap-16 lg:grid-cols-[1.25fr_0.75fr]">
          <Reveal>
            <ImageCard
              src={IMAGES.dashboard}
              alt="Painel de dados e indicadores"
              aspect="auto"
              objectFit="contain"
              className="w-full bg-white p-4 sm:p-6"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col gap-6">
              <h2 className="font-heading text-3xl font-bold leading-tight sm:text-4xl md:text-5xl text-foreground">
                O que é a UrbNet Ouvidoria
              </h2>
              <p className="text-lg leading-relaxed text-foreground">
                É o canal onde a população registra demandas e a gestão as
                organiza, distribui e acompanha até a resposta final.
              </p>
              <p className="leading-relaxed text-muted-foreground">
                Tudo fica registrado em um único painel: manifestações, prazos,
                setores responsáveis e o histórico do atendimento. Assim, a
                prefeitura responde com mais agilidade e o cidadão acompanha o
                que acontece na sua cidade.
              </p>
              <div>
                <LuxButton onClick={goToOrcamento}>Quero conhecer</LuxButton>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Feature cards */}
        <div className="relative mt-20 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => {
            const a = ACCENTS[i % 3];
            return (
              <Reveal key={f.title} delay={(i % 3) * 0.08}>
                <div className={`group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-white/70 p-7 shadow-[0_14px_40px_-30px_hsl(var(--foreground)/0.3)] ring-1 ring-black/[0.03] backdrop-blur transition-all duration-300 hover:-translate-y-1.5 ${a.cardBg} ${a.hoverShadow}`}>
                  <h3 className="font-heading text-lg font-bold leading-snug text-foreground">
                    {f.title}
                  </h3>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ------------------------------------------- Conteúdo / Como funciona */}
      <section
        id="como-funciona"
        className="relative border-y border-border/40 bg-background py-20 backdrop-blur md:py-28"
      >
        <InfrastructureLines />
        <AccentOrb color="secondary" className="absolute right-0 top-0 h-48 w-48 md:h-72 md:w-72" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6">
          <Reveal className="mb-16 text-center">
            <h2 className="font-heading text-3xl font-bold sm:text-4xl md:text-5xl text-foreground">
              Como a demanda é tratada
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-muted-foreground">
              Do registro à resposta, cada etapa é registrada para que cidadão e gestão
              saibam exatamente o que está acontecendo.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {MODULES.map((m, i) => {
              const a = ACCENTS[i % 3];
              return (
                <Reveal key={m.title} delay={(i % 2) * 0.1}>
                  <div className={`group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-white/70 p-7 text-center shadow-[0_14px_40px_-30px_hsl(var(--foreground)/0.3)] ring-1 ring-black/[0.03] backdrop-blur transition-all duration-300 hover:-translate-y-1 ${a.cardBg} ${a.hoverShadow} sm:p-9`}>
                     
                    <h3 className="relative font-heading text-xl font-bold text-foreground">
                      {m.title}
                    </h3>
                    <ul className="relative mt-6 space-y-3 text-left">
                      {m.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-sm text-muted-foreground">
                          <CheckCircle className={`mt-0.5 h-4 w-4 shrink-0 ${a.text}`} weight="duotone" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- Experiência */}
      <section className="relative mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <AccentOrb color="tertiary" className="absolute -left-10 bottom-10 h-48 w-48 md:-left-20 md:bottom-20 md:h-80 md:w-80" />
        <div className="relative grid grid-cols-1 items-start gap-16 lg:grid-cols-[1fr_1.05fr]">
          <Reveal>
            <ImageCard
              src={IMAGES.teamMeeting}
              alt="Equipe de atendimento ao cidadão"
              aspect="video"
              className="w-full"
            />
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex flex-col gap-8 lg:pt-10">
              <h2 className="font-heading text-4xl font-bold leading-tight sm:text-5xl text-foreground">
                Quem usa, sente a diferença
              </h2>
              <p className="text-lg leading-relaxed text-foreground">
                A UrbNet Ouvidoria foi pensada para quem atende e para quem é
                atendido. O cidadão registra com poucos toques. A equipe municipal
                recebe, organiza e responde com clareza.
              </p>

              {/* Carousel */}
              <div className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/50 p-8 shadow-[0_14px_40px_-28px_hsl(var(--foreground)/0.25)] ring-1 ring-black/[0.03] backdrop-blur">
                <motion.div
                  key={slide}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-center"
                >
                  <div className="inline-flex h-28 w-28 items-center justify-center rounded-3xl bg-gradient-to-br from-primary/15 to-secondary/15 shadow-inner">
                    <Lottie
                      animationData={CAROUSEL[slide].animation}
                      loop={true}
                      autoplay={true}
                      style={{ height: "100%", width: "100%" }}
                    />
                  </div>
                  <p className="mt-5 font-heading text-lg font-semibold text-foreground">
                    {CAROUSEL[slide].label}
                  </p>
                </motion.div>

                <div className="mt-7 flex items-center justify-center gap-4">
                  <button
                    onClick={prev}
                    aria-label="Anterior"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/60 bg-white/70 shadow-sm transition-all hover:scale-105 hover:bg-white"
                  >
                    <ArrowLeft className="h-4 w-4" weight="bold" />
                  </button>
                  <div className="flex gap-2">
                    {CAROUSEL.map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 rounded-full transition-all ${
                          i === slide ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30"
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={next}
                    aria-label="Próximo"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/60 bg-white/70 shadow-sm transition-all hover:scale-105 hover:bg-white"
                  >
                    <ArrowRight className="h-4 w-4" weight="bold" />
                  </button>
                </div>
              </div>

              <div>
                <LuxButton onClick={goToOrcamento}>Quero participar</LuxButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* -------------------------------------------------- Se você quer */}
      <section className="relative border-y border-border/40 py-20 md:py-28">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <img
            src={IMAGES.cityAerial}
            alt="Vista aérea da cidade"
            className="h-full w-full object-cover opacity-20"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, hsl(40 20% 98% / 0.92) 0%, hsl(40 20% 98% / 0.80) 50%, hsl(40 20% 98% / 0.95) 100%)",
            }}
          />
        </div>
        <InfrastructureLines />
        <div className="relative mx-auto max-w-6xl px-4 text-center md:px-6">
          <Reveal>
            <h2 className="font-heading text-3xl font-bold sm:text-4xl md:text-5xl text-foreground">
              O que muda no dia a dia
            </h2>
          </Reveal>

          <div className="mx-auto mt-14 max-w-4xl space-y-0">
            {BENEFITS.map((b, i) => (
              <Reveal key={b} delay={i * 0.05}>
                <div className="group flex items-center justify-center gap-4 border-b border-border/50 py-5 transition-colors hover:bg-white/30">
                  <span className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full bg-tertiary ring-4 ring-tertiary/20" />
                  <span className="font-heading text-sm font-semibold text-foreground sm:text-base">
                    {b}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-14 flex justify-center">
              <LuxButton onClick={goToApp}>Acessar plataforma</LuxButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ------------------------------------------------ Plataforma multicanal */}
      <section className="relative mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <AccentOrb color="primary" className="absolute -right-10 top-0 h-48 w-48 md:-right-20 md:h-80 md:w-80" />
        <div className="relative grid grid-cols-1 items-center gap-16 lg:grid-cols-[1fr_1.05fr]">
          <Reveal>
            <div className="flex flex-col gap-8">
              <Kicker>Tudo conectado em um só painel</Kicker>
              <h2 className="font-heading text-3xl font-bold leading-tight sm:text-4xl md:text-5xl text-foreground">
                Uma ouvidoria organizada de verdade
              </h2>
              <p className="text-lg leading-relaxed text-foreground">
                Receba demandas de vários canais, distribua para os setores certos
                e acompanhe o andamento sem perder nenhuma manifestação no caminho.
              </p>
              <p className="leading-relaxed text-muted-foreground">
                A UrbNet Ouvidoria conecta a equipe de atendimento, as secretarias
                e o cidadão em um mesmo fluxo. Com geolocalização, anexos de foto,
                status em tempo real e relatórios por período, a gestão ganha
                visão do que entrou, onde está e o que já foi resolvido.
              </p>
              <div>
                <LuxButton onClick={goToOrcamento}>Quero organizar</LuxButton>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <ImageCard
              src={IMAGES.cityAerial}
              alt="Cidade conectada digitalmente"
              aspect="square"
              className="w-full"
            />
          </Reveal>
        </div>
      </section>

      {/* ------------------------------------------------ Exemplos de serviços */}
      <section
        id="servicos"
        className="relative border-y border-border/40 bg-background py-20 backdrop-blur md:py-28"
      >
        <InfrastructureLines />
        <AccentOrb color="secondary" className="absolute left-0 top-1/4 h-48 w-48 md:h-72 md:w-72" />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6">
          <Reveal className="mb-16 text-center">
            <h2 className="font-heading text-3xl font-bold sm:text-4xl md:text-5xl text-foreground">
              Exemplo de uso no dia a dia
            </h2>
            <p className="mx-auto mt-5 max-w-4xl text-muted-foreground">
              Uma situação comum em qualquer cidade — e como a UrbNet Ouvidoria
              organiza o atendimento do início ao fim.
            </p>
          </Reveal>

          <div className="mx-auto grid max-w-sm grid-cols-1 gap-6">
            {SERVICE_EXAMPLES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.08}>
                <div className={`group relative flex h-full flex-col rounded-3xl border border-white/70 p-6 shadow-[0_14px_40px_-28px_hsl(var(--foreground)/0.25)] ring-1 ring-black/[0.03] backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_48px_-28px_hsl(var(--primary)/0.25)] ${ACCENTS[i % 3].cardBg} sm:p-8`}>
                  <div
                    className="absolute inset-0 rounded-3xl"
                    style={{
                      background:
                        "linear-gradient(150deg, hsl(0 0% 100% / 0.7) -8%, transparent 70%)",
                    }}
                  />
                  <h3 className="relative font-heading text-lg font-bold text-foreground">
                    {s.title}
                  </h3>
                  <p className="relative mt-3 text-sm leading-relaxed text-muted-foreground">
                    {s.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ O que você pode fazer */}
      <section className="relative mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <AccentOrb color="tertiary" className="absolute -left-10 bottom-10 h-48 w-48 md:-left-20 md:bottom-20 md:h-80 md:w-80" />
        <div className="relative">
          <Reveal className="mb-16 text-center">
            <h2 className="font-heading text-3xl font-bold sm:text-4xl md:text-5xl text-foreground">
              O que a UrbNet Ouvidoria permite fazer
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CAPABILITIES.map((c, i) => {
              const a = ACCENTS[i % 3];
              return (
                <Reveal key={c.title} delay={(i % 3) * 0.08}>
                  <div className={`group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-white/70 p-7 shadow-[0_14px_40px_-30px_hsl(var(--foreground)/0.3)] ring-1 ring-black/[0.03] backdrop-blur transition-all duration-300 hover:-translate-y-1 ${a.cardBg} ${a.hoverShadow} sm:p-8`}>
                    <h3 className="font-heading text-lg font-bold leading-snug text-foreground">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {c.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ Muito além da digitalização */}
      <section className="relative border-y border-border/40 py-20 backdrop-blur md:py-28">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <img
            src={IMAGES.cityHall}
            alt="Prefeitura moderna"
            className="h-full w-full object-cover opacity-15"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, hsl(40 20% 98% / 0.94) 0%, hsl(40 20% 98% / 0.82) 50%, hsl(40 20% 98% / 0.96) 100%)",
            }}
          />
        </div>
        <InfrastructureLines />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6">
          <Reveal className="mb-16 text-center">
            <h2 className="font-heading text-3xl font-bold sm:text-4xl md:text-5xl text-foreground">
              Como a UrbNet Ouvidoria se diferencia
            </h2>
            <p className="mx-auto mt-5 max-w-4xl text-muted-foreground">
              A ferramenta nasceu do dia a dia de quem atende demandas urbanas.
              Por isso, cada recurso resolve uma dor real de prefeituras e
              cidadãos.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {HIGHLIGHTS.map((h, i) => {
              const a = ACCENTS[i % 3];
              return (
                <Reveal key={h.title} delay={(i % 4) * 0.08}>
                  <div className={`group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-white/70 p-6 shadow-[0_14px_40px_-30px_hsl(var(--foreground)/0.3)] ring-1 ring-black/[0.03] backdrop-blur transition-all duration-300 hover:-translate-y-1 ${a.cardBg} ${a.hoverShadow} sm:p-7`}>
                    <h3 className="font-heading text-base font-bold leading-snug text-foreground">
                      {h.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {h.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------- CTA final */}
      <section id="cta" className="relative mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <Reveal>
            <div className="flex flex-col gap-8">
              <h2 className="font-heading text-3xl font-bold leading-tight sm:text-4xl md:text-5xl lg:text-[48px] text-foreground">
                Vamos organizar a ouvidoria da sua cidade
              </h2>
              <p className="font-heading text-lg font-medium leading-relaxed text-muted-foreground">
                Fale com a gente e entenda como a UrbNet Ouvidoria pode ajudar
                sua prefeitura a receber, organizar e responder demandas com mais
                clareza para a população.
              </p>
              <div>
                <LuxButton onClick={goToOrcamento}>Solicite um diagnóstico</LuxButton>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <ImageCard
              src={IMAGES.handshake}
              alt="Parceria entre gestão e cidadão"
              aspect="square"
              className="w-full"
            />
          </Reveal>
        </div>
      </section>

      {/* -------------------------------------------------- Trust strip */}
      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
        <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
          {[
            { icon: MapPin, k: "Demandas", l: "com localização no mapa", a: ACCENTS[0] },
            { icon: Clock, k: "Respostas", l: "mais rápidas para o cidadão", a: ACCENTS[1] },
            { icon: ShieldCheck, k: "LGPD", l: "dados protegidos", a: ACCENTS[2] },
            { icon: Tray, k: "Múltiplos canais", l: "um registro só", a: ACCENTS[0] },
          ].map((c) => (
            <div
              key={c.k}
              className={`group flex flex-col items-center rounded-2xl border border-white/60 px-4 py-7 text-center shadow-[0_8px_24px_-18px_hsl(var(--foreground)/0.2)] ring-1 ring-black/[0.03] backdrop-blur transition-all duration-300 hover:-translate-y-0.5 ${c.a.cardBg}`}
            >
              <p className="font-heading text-base font-bold text-foreground">{c.k}</p>
              <p className="text-xs text-muted-foreground">{c.l}</p>
            </div>
          ))}
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default LandingPage;
